import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getFeedbackDetail, listFeedback, triageFeedback, type FeedbackParams, type TriageStatus } from './api';

export const feedbackKeys = {
  all: ['feedback'] as const,
  list: (params: FeedbackParams) => [...feedbackKeys.all, 'list', params] as const,
  count: (params: Omit<FeedbackParams, 'page' | 'pageSize'>) => [...feedbackKeys.all, 'count', params] as const,
  detail: (id: string) => [...feedbackKeys.all, 'detail', id] as const,
};

export function useFeedbackList(params: FeedbackParams, opts: { enabled?: boolean; silent?: boolean } = {}) {
  return useQuery({
    queryKey: feedbackKeys.list(params),
    queryFn: ({ signal }) => listFeedback(params, signal),
    enabled: opts.enabled ?? true,
    placeholderData: keepPreviousData,
    meta: { silent: opts.silent ?? false },
  });
}

/** A KPI count (pageSize=1 read of the queue, until BE ask B10). Silent: a failed tile shows "—". */
export function useFeedbackCount(params: Omit<FeedbackParams, 'page' | 'pageSize'>, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: feedbackKeys.count(params),
    enabled: opts.enabled ?? true,
    queryFn: async ({ signal }) => (await listFeedback({ ...params, page: 1, pageSize: 1 }, signal)).total,
    staleTime: 60_000,
    meta: { silent: true },
  });
}

export function useFeedbackDetail(id: string) {
  return useQuery({
    queryKey: feedbackKeys.detail(id),
    queryFn: ({ signal }) => getFeedbackDetail(id, signal),
    meta: { silent: true },
  });
}

/** Triage invalidates every feedback read — the row, the counts and the detail share one status. */
export function useTriageFeedback(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ status, note }: { status: TriageStatus; note?: string }) => triageFeedback(id, status, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedbackKeys.all }),
  });
}
